import React, { useState } from "react";
import "./EmailVerificationModal.css";

const EmailVerificationModal = ({
  isOpen,
  onClose,
  onVerificationSuccess,
  API_BASE_URL,
}) => {
  const [step, setStep] = useState(1); // 1 = email input, 2 = OTP verification
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]); // Changed to 4 digits
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const maskEmail = (email) => {
    const [localPart, domain] = email.split("@");
    const maskedLocal =
      localPart.length > 4
        ? localPart.substring(0, 4) + "****"
        : localPart.substring(0, 1) + "****";
    return maskedLocal + "@" + domain;
  };

  const sendOTPRequest = async (emailToSend) => {
    const response = await fetch(`${API_BASE_URL}/otp/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: emailToSend.trim() }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message ||
          "Email address doesn't have sufficient access. Please contact Salestology Admin"
      );
    }

    return data;
  };

  const handleGenerateOTP = async () => {
    if (!email.trim() || !isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await sendOTPRequest(email);
      setMaskedEmail(maskEmail(email));
      setStep(2);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError("");
    setOtp(["", "", "", ""]); // Clear current OTP inputs - 4 digits

    try {
      await sendOTPRequest(email);
      // Show success message or keep current state
      setError(""); // Clear any existing errors
    } catch (error) {
      setError(error.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input - changed condition to < 3 for 4-digit OTP
      if (value && index < 3) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOTPKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];

      if (newOtp[index]) {
        // If current box has value, clear it
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // If current box is empty, move to previous box and clear it
        const prevInput = document.getElementById(`otp-${index - 1}`);
        if (prevInput) {
          newOtp[index - 1] = "";
          setOtp(newOtp);
          prevInput.focus();
        }
      }
    }

    // Handle arrow keys for navigation
    if (e.key === "ArrowLeft" && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }

    if (e.key === "ArrowRight" && index < 3) {
      // Changed from < 5 to < 3
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleValidateOTP = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      // Changed from 6 to 4
      setError("Please enter the complete 4-digit code"); // Updated error message
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/otp/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          otp: otpValue,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Verification successful
        onVerificationSuccess(email.trim());
        handleClose();
      } else {
        setError(data.message || "Invalid verification code");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail("");
    setOtp(["", "", "", ""]); // Changed to 4 empty strings
    setError("");
    setMaskedEmail("");
    setResendLoading(false);
    onClose();
  };

  const handleReturnHome = () => {
    window.location.href = "/";
  };
  const handleChangeEmail = () => {
    window.location.href = "/survey";
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (step === 1) {
        handleGenerateOTP();
      } else {
        handleValidateOTP();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content verification-modal">
        <h2 className="modal-title">Identify Yourself</h2>

        {step === 1 ? (
          <div className="step-content">
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              onKeyPress={handleKeyPress}
              className="email-inputverify"
              autoFocus
            />
            {error && <p className="error-message">{error}</p>}
            <button
              onClick={handleGenerateOTP}
              disabled={loading}
              className="generate-otp-button"
            >
              {loading ? "Generating..." : "Generate Code"}
            </button>
            <div className="return-home">
              <p className="return-home-text">
                Don't want to proceed?
                <strong
                  className="return-home-link"
                  onClick={handleReturnHome}
                  style={{ cursor: "pointer" }}
                >
                  Return Home
                </strong>
              </p>
            </div>
          </div>
        ) : (
          <div className="step-content">
            <p className="verification-text">
              Enter The Verification Code Sent To
              <strong> {maskedEmail}</strong>
              <p
                style={{ cursor: "pointer" }}
                className="change-email"
                onClick={handleChangeEmail}
              >
                (Change Email)
              </p>
            </p>

            <div className="otp-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  onKeyPress={handleKeyPress}
                  className="otp-input"
                  maxLength="1"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <p className="error-message">{error}</p>}
            <div className="otp-resend">
              <p className="resend-text">
                Didn't Receive The Verification Code?
                <strong
                  className="resend-code"
                  onClick={handleResendOTP}
                  style={{
                    cursor: resendLoading ? "not-allowed" : "pointer",
                    opacity: resendLoading ? 0.6 : 1,
                  }}
                >
                  {resendLoading ? "Resending..." : "Resend Code"}
                </strong>
              </p>
            </div>

            <button
              onClick={handleValidateOTP}
              disabled={loading}
              className="validate-otp-button"
            >
              {loading ? "Validating..." : "Validate Code"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationModal;
